import { useEffect, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import { useAuth } from '../../auth/AuthContext.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { actionError } from '../../components/actionError.ts';
import { useFullScreenDialog } from '../../hooks/useFullScreenDialog.ts';
import { validateRoleName } from './rolesHelpers.ts';

export interface RoleFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Fired with the new role's id once the server confirms creation. */
  onSaved: (id: number) => void;
}

export function RoleFormDialog({ open, onClose, onSaved }: Readonly<RoleFormDialogProps>): React.JSX.Element {
  const { api } = useAuth();
  const fullScreen = useFullScreenDialog();

  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setName('');
      setError('');
      setBusy(false);
      submittingRef.current = false;
    }
  }, [open]);

  const submit = async () => {
    if (submittingRef.current) return;
    const problem = validateRoleName(name);
    if (problem) {
      setError(problem);
      return;
    }
    submittingRef.current = true;
    setBusy(true);
    setError('');
    try {
      const res = await api.roles.create(name.trim());
      onSaved(res.id);
      onClose();
    } catch (e) {
      setError(actionError(e));
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" fullScreen={fullScreen}>
      <DialogTitle>Add role</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label="Role name"
            value={name}
            onChange={(e) => {
              setError('');
              setName(e.target.value);
            }}
            fullWidth
            disabled={busy}
            slotProps={{ htmlInput: { 'aria-label': 'role-name', maxLength: 64 } }}
          />
          {error && (
            <Alert role="alert" severity="error" sx={{ fontWeight: 700 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <QuietButton onClick={onClose} disabled={busy}>
          Cancel
        </QuietButton>
        <Button onClick={() => void submit()} disabled={busy}>
          Create role
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RoleFormDialog;
